/** @domain tax -- Tax calculation fields: tax code, rate, amount, jurisdiction, period, limits. */
import { z } from 'zod';
import { PRECISION_CURRENCY, PRECISION_STANDARD } from '../precision';
import { createDomainField } from './types';
import { commaFormatter, limitDecimalPlaces } from './formatters';

const define = createDomainField('tax');

export const taxCode = define({
  label: 'Tax Code',
  placeholder: 'Enter tax code',
  schema: z
    .string()
    .max(50, 'Must be at most 50 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank'),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const taxRate = define({
  label: 'Tax Rate',
  placeholder: 'Enter tax rate',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,4})?$/, 'Must be a valid rate (up to 4 decimal places)'),
  adapterHint: 'number',
  formatter: limitDecimalPlaces(4),
  displayFormat: 'percentage',
  displayPrecision: PRECISION_STANDARD,
});

export const taxAmount = define({
  label: 'Tax Amount',
  placeholder: 'Enter tax amount',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/, 'Must be a valid amount (up to 2 decimal places)'),
  adapterHint: 'text',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'currency',
  displayPrecision: PRECISION_CURRENCY,
});

export const jurisdictionLevel = define({
  label: 'Jurisdiction Level',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const taxPeriod = define({
  label: 'Tax Period',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const equityCompensationLevel = define({
  label: 'Equity Compensation Level',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

// Shared label with financial.lowerLimit — identical schema, kept in tax.ts for domain organization
export const taxLowerLimit = define({
  label: 'Lower Limit',
  placeholder: 'Enter lower limit',
  schema: z.string().regex(/^[0-9,]*$/, 'Must contain only digits'),
  adapterHint: 'text',
  formatter: commaFormatter,
  displayFormat: 'number',
});

// Shared label with financial.upperLimit — identical schema, kept in tax.ts for domain organization
export const taxUpperLimit = define({
  label: 'Upper Limit',
  placeholder: 'Enter upper limit',
  schema: z.string().regex(/^[0-9,]*$/, 'Must contain only digits'),
  adapterHint: 'text',
  formatter: commaFormatter,
  displayFormat: 'number',
});

export const residenceStatus = define({
  label: 'Residency Status',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const issuingCountry = define({
  label: 'Country of Legal Residency',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const taxCertification = define({
  label: 'Tax Certification',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'status',
  statusMap: {
    Certified: { icon: 'check_circle', tone: 'success' },
    _default: { icon: 'cancel', tone: 'error' },
  },
});

export const workState = define({
  label: 'Work State',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const residentState = define({
  label: 'Resident State',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const ficaEligible = define({
  label: 'FICA Eligible',
  schema: z.boolean(),
  adapterHint: 'checkbox',
  displayFormat: 'status',
  statusMap: {
    true: { icon: 'check_circle', tone: 'success', label: 'Eligible' },
    _default: { icon: 'cancel', tone: 'error', label: 'Not Eligible' },
  },
});

export const govDocIssueDate = define({
  label: 'Govt. Document Issue Date',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'date',
});

export const govDocExpiryDate = define({
  label: 'Govt. Document Expiry Date',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'date',
});

export const govDocIdNo = define({
  label: 'Govt. Document Id No.',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const govDocType = define({
  label: 'Govt. Document Type',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const taxRateValue = define({
  label: 'Rate',
  schema: z.number(),
  adapterHint: 'text',
  displayFormat: 'percentage',
});
